import React, { useState, useEffect } from 'react';
import { settingService } from '../../services';
import { DataTable, Input, Pill, TextareaAutosize } from '../../components';
import { useDebounce, useMessage } from '../../hooks';

/**
 * The Certificates tab: the certificate authorities this installation trusts.
 *
 * Mounted only while the tab is open, because Tabs renders the active tab's children and nothing
 * else. That is what keeps the two requests below off every other visit to Settings, and why the
 * trust state is read fresh on each visit rather than once per page load -- it describes the
 * running process, not a stored setting.
 *
 * The editable field belongs to the page's form: its value lives in formData and is saved by the
 * page's own Save button, so this component writes through the handlers it is given rather than
 * submitting anything itself.
 *
 * @param {object} props
 * @param {object} props.formData - The page's settings state.
 * @param {function} props.setFormData - Writes both certificate settings in one update.
 * @param {function} props.handleInputChange - The page's generic field handler.
 * @param {boolean} props.loading - Whether the page is still reading its settings.
 */
export const Certificates = ({ formData, setFormData, handleInputChange, loading }) => {
  // How this installation verifies TLS, and what the operating system already trusts. Not
  // both are decided by how the container was started -- so they are read separately and shown
  // read-only beside the certificates that can be managed here.
  const [certificateStatus, setCertificateStatus] = useState(null);
  // What is currently in the certificate field, read back by the API so the subject and expiry can
  // be shown before it is saved. The browser has no X.509 parser of its own.
  const [parsedCertificates, setParsedCertificates] = useState(null);

  /**
   * Reads the trust state of this installation. A failure here is not worth a message: the tab
   * simply does not show the read-only part, and everything editable still works.
   */
  const fetchCertificateStatus = async () => {
    await settingService
      .getCertificateStatus()
      .then(res => setCertificateStatus(res.certificate_status))
      .catch(() => setCertificateStatus(null));
  };

  /**
   * Reads a chosen certificate file in the browser and fills the form with it. The file is never
   * uploaded -- only its text is submitted, with the rest of the settings.
   *
   * Both settings are written in one update because handleInputChange builds its next state from
   * the current formData, so two calls in the same tick would lose the first.
   *
   * @param {object} event - The native change event from the file input.
   */
  const handleCertificateFile = event => {
    // The clear button next to the file name does not send a native event -- it calls onChange with
    // a plain { name, value } payload. Reading event.target.files would throw on it.
    const file = event?.target?.files?.[0];

    // Clearing the file removes the certificate it brought with it. Leaving the text behind under a
    // cleared file name would be a certificate nobody can account for.
    if (!file) {
      setFormData(current => ({
        ...current,
        TLS_CA_CERTIFICATES_PEM: { ...current.TLS_CA_CERTIFICATES_PEM, setting_value: '' },
        TLS_CA_CERTIFICATES_FILENAME: {
          ...current.TLS_CA_CERTIFICATES_FILENAME,
          setting_value: '',
        },
      }));
      return;
    }

    const reader = new FileReader();

    reader.onload = () =>
      setFormData(current => ({
        ...current,
        TLS_CA_CERTIFICATES_PEM: {
          ...current.TLS_CA_CERTIFICATES_PEM,
          setting_value: String(reader.result),
        },
        TLS_CA_CERTIFICATES_FILENAME: {
          ...current.TLS_CA_CERTIFICATES_FILENAME,
          setting_value: file.name,
        },
      }));

    reader.onerror = () => showMessage(`Could not read ${file.name}`, 'error');

    reader.readAsText(file);
  };

  // Describe whatever is in the certificate field, a moment after typing stops. Debounced
  // because the field is edited by pasting, and every keystroke would otherwise be a request.
  const { debounce: debouncedParseCertificates } = useDebounce(
    value =>
      settingService
        .parseCertificates(value)
        .then(res => setParsedCertificates(res))
        .catch(() => setParsedCertificates(null)),
    400,
    []
  );

  useEffect(() => {
    const value = formData?.TLS_CA_CERTIFICATES_PEM?.setting_value;

    if (!value || !String(value).trim()) {
      setParsedCertificates(null);
      return;
    }

    debouncedParseCertificates(value);
  }, [formData?.TLS_CA_CERTIFICATES_PEM?.setting_value, debouncedParseCertificates]);

  // A certificate read from a file is shown as coming from that file, and the text is left as the
  // file gave it. Clearing the file is what makes the field editable again -- so the name on screen
  // always describes what is actually in the field, rather than being quietly dropped when the text
  // is typed over.
  const hasCertificateFile = !!formData?.TLS_CA_CERTIFICATES_FILENAME?.setting_value;

  // What is wrong with the pasted text, in the shape the form components use. Only the first
  // problem is reported: a value that cannot be read at all is one problem, however many blocks
  // it has.
  const certificateParseValidation = !parsedCertificates
    ? undefined
    : parsedCertificates.private_key_blocks
      ? {
          valid: false,
          message:
            'This contains a private key. Only the certificate authority certificate is needed here, and a private key must not be stored in a setting. Paste only the blocks running from -----BEGIN CERTIFICATE----- to -----END CERTIFICATE-----.',
        }
      : parsedCertificates.invalid_blocks?.length
        ? {
            valid: false,
            message: `Block ${parsedCertificates.invalid_blocks[0].index + 1} is not a readable certificate (${parsedCertificates.invalid_blocks[0].reason}). Each block runs from -----BEGIN CERTIFICATE----- to -----END CERTIFICATE-----.`,
          }
        : parsedCertificates.certificates.length === 0
          ? {
              valid: false,
              message:
                'No certificate found. Paste the PEM text, which starts with -----BEGIN CERTIFICATE-----.',
            }
          : parsedCertificates.trailing_text
            ? {
                valid: false,
                message:
                  'There is text outside the certificate blocks, which usually means the value was cut short when it was pasted.',
              }
            : undefined;

  const certificateValidation =
    certificateParseValidation ||
    (hasCertificateFile
      ? {
          valid: 'info',
          message: 'Read from file. Clear the file to paste a certificate instead.',
        }
      : undefined);

  // Every trusted certificate in one list, whatever supplied it.
  const certificateStatusValues = {
    valid: { label: 'Valid', className: 'pill' },
    expired: {
      label: 'Expired',
      className: 'pill pill-full-red',
      tooltip: 'No longer valid, and not trusted.',
    },
    not_yet_valid: {
      label: 'Not yet valid',
      className: 'pill pill-red',
      tooltip: 'Becomes valid at a later date.',
    },
    expiring_soon: {
      label: 'Expires soon',
      className: 'pill pill-yellow',
      tooltip: 'Still trusted, but close enough to its expiry date to need renewing.',
    },
    not_ca: {
      label: 'Not a CA',
      className: 'pill pill-red',
      tooltip:
        "A server's own certificate, which cannot sign others. Use the certificate of the authority that issued it.",
    },
  };

  const certificateRows = [
    ...(parsedCertificates?.certificates || []).map((certificate, index) => ({
      id: `runtime-${index}`,
      subject: certificate.subject,
      source: certificate.sourceLabel,
      validFrom: new Date(certificate.validFrom).toLocaleDateString('sv-SE'),
      expires: new Date(certificate.validTo).toLocaleDateString('sv-SE'),
      // The value being edited has not been saved, so its file name is whatever is in the form
      // rather than whatever the API last stored.
      filename: formData?.TLS_CA_CERTIFICATES_FILENAME?.setting_value || '-',
      status: certificate.status,
    })),
    ...(certificateStatus?.os?.certificates || []).map((certificate, index) => ({
      id: `os-${index}`,
      subject: certificate.subject,
      source: certificate.sourceLabel,
      validFrom: new Date(certificate.validFrom).toLocaleDateString('sv-SE'),
      expires: new Date(certificate.validTo).toLocaleDateString('sv-SE'),
      filename: certificate.filename,
      status: certificate.status,
    })),
  ];

  useEffect(() => {
    fetchCertificateStatus();
  }, []);

  const { showMessage } = useMessage();

  return (
    <>
      <div className="form-light-text">
        Configure the certificate authorities this Katalogue instance trusts, in addition to the
        default public ones every browser trusts.
        {`\n`}
        Only runtime certificates can be added here. Connectors like ODBC, IBM DB2 and Oracle
        require certificates set in the operating system's trust store.
      </div>

      <div className="form-content">
        {certificateStatus?.verificationDisabled && (
          <div className="form-block vertical">
            <Pill
              label="Certificate verification is disabled"
              className="pill-full-red"
              tooltip="NODE_TLS_REJECT_UNAUTHORIZED=0 is set for this installation."
            />
            <div className="form-light-text">
              Every outbound connection is accepted without checking its certificate, not only the
              one that needed a private certificate authority. Add the certificate below and remove
              the <code>NODE_TLS_REJECT_UNAUTHORIZED</code> environment variable.
            </div>
          </div>
        )}

        {certificateStatus?.canApplyCertificates === false && (
          <div className="form-block vertical">
            <div className="form-light-text">
              Node reports no trusted roots, which means it is verifying through a certificate store
              it cannot read
              {certificateStatus?.usesOpenSslStore && (
                <>
                  {', because '}
                  <code>--use-openssl-ca</code>
                  {' is set'}
                </>
              )}
              . Certificates added here are not applied in this state — applying them would replace
              that store with them alone. Install the certificate in the host trust store instead.
            </div>
          </div>
        )}

        <div className="form-block vertical">
          <TextareaAutosize
            type="text"
            name={formData?.TLS_CA_CERTIFICATES_PEM?.setting_code}
            label={formData?.TLS_CA_CERTIFICATES_PEM?.setting_name}
            value={formData?.TLS_CA_CERTIFICATES_PEM?.setting_value || ''}
            onChange={handleInputChange}
            disabled={
              loading ||
              formData?.TLS_CA_CERTIFICATES_PEM?.setting_is_disabled ||
              certificateStatus?.canApplyCertificates === false ||
              hasCertificateFile
            }
            title={formData?.TLS_CA_CERTIFICATES_PEM?.setting_description}
            isOptional={true}
            minRows={8}
            maxRows={20}
            placeholder="-----BEGIN CERTIFICATE-----"
            validation={certificateValidation}
          />
        </div>

        <div className="form-block vertical">
          <Input
            type="file"
            name="certificate_file"
            label="Read from a file"
            accept=".pem,.crt,.cer"
            value={formData?.TLS_CA_CERTIFICATES_FILENAME?.setting_value || ''}
            onChange={handleCertificateFile}
            disabled={
              loading ||
              formData?.TLS_CA_CERTIFICATES_PEM?.setting_is_disabled ||
              certificateStatus?.canApplyCertificates === false
            }
            title="The file is read here in your browser. It is not uploaded — only the certificate text is saved."
            isOptional={true}
          />
        </div>
      </div>

      <h4>Installed Certificates</h4>

      {certificateRows.length === 0 ? (
        <div className="form-light-text">
          No extra certificate authorities are installed. Only trusting the default public ones
          every browser trusts.
        </div>
      ) : (
        <>
          <DataTable
            data={certificateRows}
            idColumn="id"
            loading={loading}
            editable={false}
            hideToolbar={true}
            columns={[
              {
                id: 'subject',
                name: 'Subject',
                description: 'Who the certificate was issued to.',
              },
              {
                id: 'source',
                name: 'Source',
                className: 'small-text',
                description: `Where the certificate comes from.\nRuntime: applied by Katalogue as it starts. Comes from these settings or environment variable.\nOperating system: already in the host's trust store, which Katalogue reads but cannot change.`,
              },
              {
                id: 'validFrom',
                name: 'Valid From',
                className: 'small-text',
                description: 'The first day the certificate is valid.',
              },
              {
                id: 'expires',
                name: 'Expires At',
                className: 'small-text',
                description: 'The last day the certificate is valid.',
              },
              {
                id: 'filename',
                name: 'Filename',
                className: 'small-text',
                description: 'The file the certificate was read from, where one is known.',
              },
              {
                id: 'status',
                name: 'Status',
                type: 'pill',
                value: 'status',
                values: certificateStatusValues,
                description:
                  'What the certificate says about itself. Expired, Not yet valid and Not a CA cannot vouch for a server at all; Expires soon still can, for now.',
              },
            ]}
          />
        </>
      )}
    </>
  );
};
